import type {CalendarHeatmapDay} from 'shells/calendar/calendar-heatmap/types';
import type {
    AvailabilityLevel,
    MatrixHeatmapCell,
    MatrixHeatmapRowData,
} from 'shells/table/matrix-heatmap/types';

import type {
    AvailabilityReportFragment,
    AvailabilityReportDayFragment,
} from 'nutshell-graphql-types';

import type {DailyPerformanceSummaryDateData} from './daily-performance-summary/daily-performance-summary-modal';
import type {ViewBookingHeatmapType} from './types';

const SCORE_TO_LEVEL: Record<string, AvailabilityLevel> = {
    LOW: 'low',
    MEDIUM: 'medium',
    HIGH: 'high',
    NOT_AVAILABLE: 'unavailable',
};

function formatShortDate(dateString: string): string {
    const date = new Date(dateString + 'T00:00:00');

    return date.toLocaleDateString(undefined, {month: 'short', day: 'numeric'});
}

export function formatLongDate(dateString: string): string {
    const date = new Date(dateString + 'T00:00:00');

    return date.toLocaleDateString(undefined, {month: 'long', day: 'numeric', year: 'numeric'});
}

function formatDayDataTip(day: AvailabilityReportDayFragment): string {
    const viewCount = day.views.count;
    const bookingCount = day.bookings.count;
    const viewLabel = viewCount === 1 ? 'view' : 'views';
    const bookingLabel = bookingCount === 1 ? 'booking' : 'bookings';
    const numViews = viewCount.toLocaleString();
    const numBookings = bookingCount.toLocaleString();

    return `${numViews} ${viewLabel}, ${numBookings} ${bookingLabel}, ${day.conversionRate} conversion rate`;
}

const NO_DATA_CELL: MatrixHeatmapCell = {level: 'noData', dataTip: 'No data'};

export function mapReportToRows(
    report: AvailabilityReportFragment,
    maxColumns: number,
    maxRows: number
): MatrixHeatmapRowData[] {
    const days = maxRows ? report.days.slice(-maxRows) : report.days;

    return [...days].reverse().map((day) => {
        const cells: MatrixHeatmapCell[] = day.availabilitySnapshot.map((snap) => ({
            level: SCORE_TO_LEVEL[snap.score] ?? 'unavailable',
            dataTip: snap.dataTip,
        }));

        while (cells.length < maxColumns) {
            cells.push(NO_DATA_CELL);
        }

        return {
            id: day.date,
            primaryText: formatShortDate(day.date),
            secondaryText: `${day.views.count} views`,
            metricText: day.conversionRate,
            metricDataTip: `${day.bookings.count.toLocaleString()} bookings`,
            cells,
        };
    });
}

export function mapReportToHeatmapDates(
    report: AvailabilityReportFragment,
    type: ViewBookingHeatmapType
): CalendarHeatmapDay[] {
    return report.days.map((day) => ({
        date: day.date,
        isBookable: day.isBookable,
        count: type === 'views' ? day.views.count : day.bookings.count,
        heatmap: type === 'views' ? day.views.heatmap : day.bookings.heatmap,
        dataTip: formatDayDataTip(day),
    }));
}

export function getDayDataByDate(
    day: AvailabilityReportDayFragment | null
): DailyPerformanceSummaryDateData | null {
    if (!day) {
        return null;
    }

    return {
        dateString: day.date,
        numViews: day.views.count,
        numBookings: day.bookings.count,
        isBookable: day.isBookable,
        forecastCells: day.availabilitySnapshot.map((snapshot) => ({
            level: SCORE_TO_LEVEL[snapshot.score] ?? 'unavailable',
            date: formatShortDate(snapshot.date),
        })),
    };
}
